import { Injectable } from '@nestjs/common';
import { ChatSession } from '@chat/prisma-client';
import { PrismaService } from '../prisma/prisma.service';
import { ChatLink, ChatReply, ChatResponder } from './chat-responder';
import {
  CONFIDENT_THRESHOLD,
  KnowledgeIndex,
  UNCERTAIN_THRESHOLD,
  buildIndex,
  fallbackSuggestions,
  isAmbiguous,
  scoreQuery,
  toSuggestion,
} from './retrieval/knowledge-index';

const FALLBACK_MESSAGE =
  "I'm not sure about that one — could you rephrase, or check the FAQ for more detail?";

const MAX_SUGGESTIONS = 3;

// The index is rebuilt from scratch per application rather than per message.
// Entries can now be authored live (KnowledgeController), not just via the
// seed script, but a short TTL is still simpler than wiring an invalidation
// hook from writes to this cache. Per-process and deliberately dumb: a
// stale answer for up to a minute after an edit is not worth a
// cache-coherence problem.
const INDEX_TTL_MS = 60_000;

@Injectable()
export class ScriptedChatResponder implements ChatResponder {
  readonly key = 'scripted';

  private readonly indexCache = new Map<string, { index: KnowledgeIndex; expiresAt: number }>();

  constructor(private readonly prisma: PrismaService) {}

  async respond(session: ChatSession, message: string): Promise<ChatReply> {
    const index = await this.loadIndex(session.applicationId);
    const ranked = scoreQuery(index, message);
    const best = ranked[0];

    if (!best || best.confidence < UNCERTAIN_THRESHOLD) {
      return {
        content: FALLBACK_MESSAGE,
        metadata: { matched: false, suggestions: fallbackSuggestions(index, MAX_SUGGESTIONS) },
      };
    }

    // Below the confident bar, or too close to the runner-up to call: still
    // answer with the best match, but show what else it could have been rather
    // than delivering a coin-flip as though it were certain.
    const confident = best.confidence >= CONFIDENT_THRESHOLD && !isAmbiguous(ranked);
    const alternatives = confident
      ? []
      : ranked.slice(1, 1 + MAX_SUGGESTIONS).map(({ entry }) => toSuggestion(entry));

    const links = toLinks(best.entry.links);

    return {
      content: applyName(best.entry.answer, session.externalUserName),
      metadata: {
        matched: true,
        question: best.entry.question,
        confidence: round(best.confidence),
        ...(alternatives.length > 0 ? { suggestions: alternatives } : {}),
      },
      ...(links.length > 0 ? { links } : {}),
    };
  }

  private async loadIndex(applicationId: string): Promise<KnowledgeIndex> {
    const cached = this.indexCache.get(applicationId);
    if (cached && cached.expiresAt > Date.now()) return cached.index;

    const entries = await this.prisma.knowledgeEntry.findMany({
      where: { applicationId, isActive: true },
      orderBy: { sortOrder: 'asc' },
    });

    const index = buildIndex(entries);
    this.indexCache.set(applicationId, { index, expiresAt: Date.now() + INDEX_TTL_MS });

    return index;
  }
}

// Persisted onto every assistant message, so keep it readable rather than
// carrying float noise into the database.
function round(value: number): number {
  return Math.round(value * 1000) / 1000;
}

// `{{User}}` in an entry's answer (e.g. kb_demo_greeting) is filled in from
// the shopper's own identify()-provided name — first name only ("Hi
// Sudharshan!" reads as a greeting; "Hi Sudharshan Reddy!" reads like a form
// letter, same reasoning as user-dashboard's chat-widget-embed.component.ts).
// Anonymous shoppers (no externalUserName) get "there" rather than a blank
// or a literal "{{User}}" leaking into the reply.
function applyName(answer: string, externalUserName: string | null | undefined): string {
  if (!answer.includes('{{User}}')) return answer;

  const firstName = externalUserName?.trim().split(/\s+/)[0];
  return answer.replace(/\{\{User\}\}/g, firstName || 'there');
}

// entry.links is raw stored Json — validated defensively (like any
// untrusted stored data) rather than trusted to already be well-shaped, so
// a malformed row (hand-edited, or written before this field existed —
// entries default to `[]`) degrades to "no links" instead of throwing.
function toLinks(value: unknown): ChatLink[] {
  if (!Array.isArray(value)) return [];

  return value.filter(
    (item): item is ChatLink =>
      typeof item === 'object' &&
      item !== null &&
      typeof (item as ChatLink).label === 'string' &&
      typeof (item as ChatLink).url === 'string',
  );
}
